import { Checkbox, InputField, Tabs } from "@deathstar/ui";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { matchSorter } from "match-sorter";
import { useMemo, useState } from "react";
import api from "../../api/api";
import { useAccountId } from "../../api/useAccountId";

export function DriverLinker({
    open,
    currentValue,
    onChange,
    onClose,
}: {
    open: boolean;
    currentValue: { id: number }[];
    onChange(value: { id: number }[]): unknown;
    onClose(): void;
}) {
    const accountId = useAccountId();
    const { data } = api.drivers.useFind(accountId!);
    const [filter, setFilter] = useState<"company" | "ic" | "all">("all");
    const [search, setSearch] = useState("");
    const filtered = useMemo(
        () =>
            matchSorter(
                (data || []).filter((r) =>
                    filter === "all" ? true : filter === "company" ? !r.policyOwnerOperator : r.policyOwnerOperator
                ),
                search,
                { keys: ["name", "license"] }
            ),
        [data, search, filter]
    );

    return (
        <Dialog open={open} onClose={onClose} classes={{ paper: "p-4 w-screen max-w-lg" }}>
            <Tabs<typeof filter>
                tabs={[
                    { label: () => <div className="min-w-16 px-2">All</div>, value: "all", buttonProps: { className: "z-[1] grow" } },
                    {
                        label: () => <div className="min-w-16 px-2">Company</div>,
                        value: "company",
                        buttonProps: { className: "z-[1] grow" },
                    },
                    {
                        label: () => <div className="min-w-16 px-2">Independent contractors</div>,
                        value: "ic",
                        buttonProps: { className: "z-[1] grow" },
                    },
                ]}
                value={filter}
                onChange={setFilter}
                className="mb-1 rounded-md bg-stone-100 py-1 text-sm shadow-inner"
                borderClasses="bg-white border border-waypoint-blue z-[0] shadow rounded-md"
            />
            <InputField
                startAdornment={<MagnifyingGlassIcon className="h-4 w-4 text-stone-400" />}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                classes={{ inputContainer: "py-1", root: "mb-4 sticky top-2", input: "placeholder:!text-stone-400" }}
                placeholder="Search..."
            />
            <table className="table-auto text-sm col-spacing-4 row-p-2 row-spacing-2">
                <thead>
                    <tr>
                        <th></th>
                        <th className="text-left text-xs font-light uppercase text-gray-500">Name</th>
                        <th className="text-left text-xs font-light uppercase text-gray-500">License</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((driver) => {
                        const isSelected = currentValue.some((t) => t.id === driver.id);
                        return (
                            <tr
                                key={driver.id}
                                className="cursor-pointer hover:bg-gray-100 [&>td:first-child]:rounded-l [&>td:last-child]:rounded-r"
                                onClick={async () => {
                                    if (isSelected) {
                                        onChange(currentValue.filter((t) => t.id !== driver.id));
                                    } else {
                                        onChange([...currentValue, { id: driver.id }]);
                                    }
                                }}
                            >
                                <td>
                                    <Checkbox classes={{ input: "w-4 h-4" }} checked={isSelected} readOnly />
                                </td>
                                <td>{driver.name}</td>
                                <td>{driver.license}</td>
                            </tr>
                        );
                    }) || (
                        <>
                            <tr>
                                <td colSpan={3}>
                                    <Skeleton variant="rect" width="100%" height={32} />
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={3}>
                                    <Skeleton variant="rect" width="100%" height={32} />
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={3}>
                                    <Skeleton variant="rect" width="100%" height={32} />
                                </td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>
        </Dialog>
    );
}
